import { useCallback } from 'react';
import { useAppStore } from '@/store';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import { Screen } from '@/types';

import type { IAppStore } from '@/store';
import useAuth from './useAuth';
import useHideBackButton from './useHideBackButton';

const SCREEN_TITLES: Record<Screen, string> = {
  [Screen.Library]: 'GenAI Studio',
  [Screen.Create]: 'Select WoW Presets',
  [Screen.Loading]: 'Generating...',
  [Screen.Files]: 'Your Creations',
};

// External selector for stable reference
const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  previousScreen: store.previousScreen,
  seeAllCategorySlug: store.seeAllCategorySlug,
  setSeeAllCategorySlug: store.setSeeAllCategorySlug,
  seeAllCategoryTitle: store.seeAllCategoryTitle,
});

/**
 * Hook for Header component state and handlers.
 */
const useHeaderState = () => {
  const {
    screen,
    setScreen,
    previousScreen,
    seeAllCategorySlug,
    setSeeAllCategorySlug,
    seeAllCategoryTitle,
  } = useAppStore(selector);
  const { isAuthorized } = useAuth();

  const isSeeAllOpen = screen === Screen.Library && seeAllCategorySlug !== null;

  // Read hideBackButton deeplink param once (stable across re-renders)
  const hideBackButton = useHideBackButton();

  // Back button shown on mobile only (CSS handles hiding on desktop)
  // Hide on library screen, and on create screen when hideBackButton deeplink param is true
  const showBackButton =
    isSeeAllOpen ||
    (screen !== Screen.Library &&
      !(screen === Screen.Create && hideBackButton));

  // Generations button shown on mobile only (CSS handles hiding on desktop)
  // Only show for library and create screens
  const showGenerationsButton =
    screen === Screen.Library || screen === Screen.Create;

  const handleBack = useCallback(() => {
    // Send BACK analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });

    if (isSeeAllOpen) {
      setSeeAllCategorySlug(null);
      return;
    }

    if (screen === Screen.Create) {
      // Don't reset - preserve uploaded media for when user selects another preset
      // Reset canvas load state so images can be refreshed on next preset selection
      // Gallery stays mounted, so its scroll/active category are preserved
      // natively — no shouldScrollToActiveTab flag needed (and setting it would
      // pin the active tab, since nothing remounts to consume/reset it).
      resetCanvasLoadState();
      setScreen(Screen.Library);
    } else if (screen === Screen.Files) {
      // Go back to previous screen if available, otherwise library
      if (previousScreen && previousScreen !== Screen.Files) {
        setScreen(previousScreen);
      } else {
        setScreen(Screen.Library);
      }
    }
  }, [screen, setScreen, previousScreen, isSeeAllOpen, setSeeAllCategorySlug]);

  const handleGenerationsClick = useCallback(() => {
    setScreen(Screen.Files);
  }, [setScreen]);

  const handleCloseApp = useCallback(() => {
    // Send CANCEL analytics
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.CANCEL,
      screen_type: getScreenType(),
    });

    try {
      closeMiniapp();
    } catch {
      // Failed to close miniapp - SDK may not be available
    }
  }, []);

  // Mobile header title: the open See-All category, otherwise the screen title.
  // Until the See-All section (and its title) resolves, this falls through to
  // the screen title — no separate fallback literal.
  const getMobileTitle = useCallback(
    () =>
      isSeeAllOpen && seeAllCategoryTitle
        ? seeAllCategoryTitle
        : SCREEN_TITLES[screen],
    [screen, isSeeAllOpen, seeAllCategoryTitle]
  );

  return {
    screen,
    isSeeAllOpen,
    isAuthorized,
    showBackButton,
    showGenerationsButton,
    handleBack,
    handleGenerationsClick,
    handleCloseApp,
    getMobileTitle,
  };
};

export default useHeaderState;
